'use client'

import dynamic from 'next/dynamic'
import { useState, useMemo } from 'react'
import { useLogin } from '@/hooks/HookLogin'
import { useRouter } from 'next/navigation'
import { useBreakpoints } from '@/hooks/HookUseMobile'
import { MobileLoginForm } from './MobileLoginForm'
import { Eye, EyeClosed} from 'lucide-react'


const Map = dynamic(() => import('@/app/components/DesktopEnglish/login/LoginMap').then(mod => mod.Map), {
  ssr: false,
  loading: () => (
    <div className="absolute inset-0 z-0 flex items-center justify-center bg-gray-100">
      <p>Loading map...</p>
    </div>
  )
})


type DesktopLoginFormProps = {
  nationid: string
  password: string
  setNationid: (value: string) => void
  setPassword: (value: string) => void
  handleSubmit: (e: React.FormEvent) => void
  isPending: boolean
  isError: boolean
  mutationError: unknown
}


const DesktopLoginForm = ({
  nationid,
  password,
  setNationid,
  setPassword,
  handleSubmit,
  isPending,
  isError,
  mutationError,
}: DesktopLoginFormProps) => {

  const [showPass, setShowPass] = useState(false)



  return (
    <>
      <div className="fixed inset-0 flex items-center justify-center pointer-events-auto backdrop-blur-xs"></div>
      <div className="fixed z-10 backdrop-blur-2xl bg-white/30 left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-[5px] shadow-xl shadow-neutral-500/40 bg-white/10 flex items-center justify-center w-[340px] sm:w-[400px]">
        <form
          onSubmit={handleSubmit}
          className="bg-transparent p-8 rounded-[5px] shadow-2xl shadow-neutral-500/60 w-[450px] max-w-md"
        >
          <h1 className="text-2xl font-bold mb-10 text-center text-neutral-700">
            Locker Platform
          </h1>


          <div className="mb-4">
            <div className="relative w-full">
              <input
                type="text"
                id="nationid"
                name="nationid"
                placeholder=" "
                value={nationid}
                onChange={(e) => setNationid(e.target.value)}
                disabled={isPending}
                className="peer w-full pt-5 pb-2 border-b border-neutral-400 focus:outline-none focus:border-[#0095da] transition-all duration-300 bg-transparent"
              />
              <label
                htmlFor="nationid"
                className="absolute left-0 top-5 text-neutral-700 text-lg transition-all duration-300 peer-focus:-translate-y-5 peer-focus:text-sm peer-focus:font-normal peer-focus:text-neutral-600 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:text-lg peer-placeholder-shown:font-semibold peer-placeholder-shown:text-neutral-500 peer-[&:not(:placeholder-shown)]:-translate-y-5 peer-[&:not(:placeholder-shown)]:text-sm peer-[&:not(:placeholder-shown)]:text-neutral-600 pointer-events-none"
              >
                Nation ID
              </label>
            </div>
          </div>


          <div className="mb-6 mt-8">
            <div className="relative w-full">
              <input
                type={showPass ? "text" : "password"}
                id="password"
                name="password"
                required
                placeholder=" "
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isPending}
                className="peer w-full pt-5 pb-2 border-b border-neutral-400 focus:outline-none focus:border-[#0095da] transition-all duration-300 bg-transparent"
              />
              <label
                htmlFor="password"
                className=" absolute left-0 top-5 text-neutral-700 text-lg transition-all duration-300 peer-focus:-translate-y-5 peer-focus:text-sm peer-focus:font-normal peer-focus:text-neutral-600 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:text-lg peer-placeholder-shown:font-semibold peer-placeholder-shown:text-neutral-500 peer-[&:not(:placeholder-shown)]:-translate-y-5 peer-[&:not(:placeholder-shown)]:text-sm peer-[&:not(:placeholder-shown)]:text-neutral-600 pointer-events-none"
              >
                Password

              </label>
              <button
                type="button"
                onClick={() => setShowPass((prev) => !prev)}
                className="absolute right-0 top-9 -translate-y-1/2 p-1 text-neutral-400 hover:text-neutral-600 transition-colors"
              >
                {showPass ? (
                  <Eye className="h-5 w-5" strokeWidth={1.75} />
                ) : (
                  <EyeClosed className="h-5 w-5" strokeWidth={1.75} />
                )}
              </button>
            </div>
          </div>


          {isError && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg text-sm">
              {(mutationError as any)?.response?.data?.message || 'Login failed'}
            </div>
          )}


          <button
            type="submit"
            disabled={isPending}
            className="w-full hover:bg-[#0095da] bg-[#0095da] text-white font-semibold py-3 px-4 rounded-lg disabled:opacity-50 transition-all hover:shadow-xl mt-10 cursor-pointer"
          >
            {isPending ? 'Logging in...' : 'Login'}
          </button>
        </form>
      </div>
    </>
  )
}

export const Login = () => {
  const router = useRouter()
  const { mutate, isPending, isError, error: mutationError } = useLogin()

  const breakpoints = useMemo(() => ({ isMobile: 640 }), [])


  const { isMobile = false, mounted } = useBreakpoints(breakpoints)

  const [nationid, setNationid] = useState('')
  const [password, setPassword] = useState('')

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    mutate(
      { nationid, password },
      {
        onSuccess: () => {
          router.push('/')
        },
      }
    )
  }

  const showMobile = mounted && isMobile

  return (
    <div className="relative h-screen w-full overflow-hidden">

      {!showMobile && <Map className="absolute inset-0 z-0" />}

      <div className="relative z-10 h-full w-full">
        {showMobile ? (
          <MobileLoginForm
            nationid={nationid}
            password={password}
            setNationid={setNationid}
            setPassword={setPassword}
            handleSubmit={handleSubmit}
            isPending={isPending}
            isError={isError}
            mutationError={mutationError}
          />
        ) : (
          <DesktopLoginForm
            nationid={nationid}
            password={password}
            setNationid={setNationid}
            setPassword={setPassword}
            handleSubmit={handleSubmit}
            isPending={isPending}
            isError={isError}
            mutationError={mutationError}
          />
        )}
      </div>
    </div>
  )
}
